<template>
  <button class="button" @click.prevent="$emit('click')" :disabled="disabled">
    {{ text }}
    <span
      class="loader"
      :style="`border: 2px solid ${textColor}; border-bottom-color: transparent`"
      v-if="loading"
    ></span>
  </button>
</template>
<script>
export default {
  props: {
    text: { type: String },
    loading: { type: Boolean, default: false },
    textColor: { type: String, default: "#fff" },
    disabled: { type: Boolean, default: false },
  },
};
</script>
<style lang="scss" scoped>
.button {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 7.5rem;
  width: 100%;
  border: 0.5px solid transparent;
  border-radius: 12px;
  @include type($fw: 700);
  padding: 0 10px;
  transition: all 0.3s ease;
  cursor: pointer;
  @media (max-height: 499px) and (max-width: 899px), (max-width: $sm) {
    height: 6rem;
    font-size: 14px;
  }
  &[disabled] {
    pointer-events: none;
    opacity: 0.7;
  }
  .loader {
    display: inline-block;
    margin-left: 10px;
    border-radius: 100%;
    -webkit-animation-fill-mode: both;
    animation-fill-mode: both;
    height: 20px;
    width: 20px;
    max-width: 20px;
    max-height: 20px;
    min-height: 20px;
    min-width: 20px;
    background: transparent !important;
    -webkit-animation: rotate 0.75s 0s linear infinite;
    animation: rotate 0.75s 0s linear infinite;
  }
  &--xxl {
    font-family: $fontBodoni;
    height: 9rem;
    padding: 0 60px;
    font-size: 2rem;
    font-weight: 600;
    line-height: 1.5;
    @media (max-width: $xxs) {
      height: 5.5rem;
      padding: 0 24px;
    }
    @media (max-width: $xxxs) {
      font-size: 1.6rem;
    }
  }
  &--xl {
    font-family: $fontBodoni;
    height: 7rem;
    padding: 0 60px;
    font-size: 2rem;
    font-weight: 600;
    line-height: 1.5;
    @media (max-width: $xxs) {
      height: 5.5rem;
      padding: 0 24px;
    }
    @media (max-width: $xxxs) {
      font-size: 1.6rem;
    }
  }
  &--lg {
    font-family: $fontBodoni;
    height: 7rem;
    padding: 0 24px;
    font-size: 2rem;
    font-weight: 600;
    line-height: 1.5;
    @media (max-width: $xxs) {
      height: 5.5rem;
      padding: 0 12px;
    }
    @media (max-width: $xxxs) {
      font-size: 1.6rem;
    }
  }
  &--ml {
    font-family: $fontBodoni;
    height: 5rem;
    padding: 0 24px;
    font-size: 2.1rem;
    font-weight: 600;
    line-height: 1.5;
    @media (max-width: $xxs) {
      height: 4.8rem;
      font-size: 2rem;
    }
  }
  &--ms {
    font-family: $fontOpenSans;
    height: 4.8rem;
    padding: 0 20px;
    font-size: 17px;
    font-weight: 600;
    line-height: 1.35;
    text-transform: uppercase;
    @media (max-width: $xxs) {
      height: 4.2rem;
    }
  }
  &--sm {
    width: 100%;
    min-width: 18rem;
    max-width: 19rem;
    height: 5.5rem;
    font-size: 12px;
    border-radius: 7px;
  }
  &--xs {
    width: 100%;
    min-width: 12rem;
    max-width: 14rem;
    height: 4.1rem;
    font-size: 12px;
    border-radius: 7px;
  }
  &--black {
    background-color: $black;
    color: $white;
    &:hover {
      background-color: $orange;
    }
  }
  &--black-grey {
    background-color: $lightBlack;
    color: $white;
    &:hover {
      background-color: rgba($color: $lightBlack, $alpha: 0.8);
    }
  }
  &--white {
    background-color: $white;
    color: $black;
    &:hover {
      background-color: $light2Grey;
    }
  }
  &--green {
    background-color: $green;
    color: $white;
    &:hover {
      background-color: $orange;
    }
  }
  &--ellipse {
    border-radius: 34px;
  }
  &--outline {
    background-color: transparent;
    color: $black;
    border-color: $black;
    &:hover {
      background-color: rgba($color: $black, $alpha: 0.1);
      color: $black;
    }
  }
  &--outline-white {
    background-color: transparent;
    color: $white;
    border-color: $white;
    &:hover {
      background-color: rgba($color: $white, $alpha: 0.1);
      color: $white;
    }
  }
  &--outline-grey {
    background-color: transparent;
    color: $grey;
    border-color: $grey;
    &:hover {
      border-color: $black;
      background-color: rgba($color: $grey, $alpha: 0.1);
      color: $black;
    }
  }
  &--outline-black-grey {
    color: $white;
    background: rgba($color: $black, $alpha: 0.5);
    border-width: 0.8px;
    border-color: $light2Grey;
  }
  &--uppercase {
    text-transform: uppercase;
  }

  &--oval {
    height: 6.8rem;
    width: max-content;
    border-radius: 34px;
    padding: 18px 35px;
    font-size: 21px;
    font-weight: 400;
    cursor: pointer;
    &.button--xxl {
      height: 9.8rem;
      border-radius: 100px;
      font-size: 4rem;
      padding: 0 1rem;
      @media only screen and (max-width: $lg) {
        height: 7rem;
        border-radius: 100px;
        font-size: 3rem;
      }
      @media only screen and (max-width: $sm) {
        font-size: 24px;
      }
    }

    @media only screen and (max-width: $xxxs) {
      height: 5.2rem;
      padding: 12px 25px;
      font-size: 18px;
    }
  }

  &--only-text {
    width: auto;
    height: auto;
    border: none;
    @include type();
    color: $lightGrey;
    @media (max-height: 499px) and (max-width: 899px), (max-width: $sm) {
      font-size: 14px;
    }
    &:hover {
      color: $blue;
    }
  }
}

@keyframes rotate {
  0% {
    -webkit-transform: rotate(0deg) scale(1);
    transform: rotate(0deg) scale(1);
  }
  50% {
    -webkit-transform: rotate(180deg) scale(0.6);
    transform: rotate(180deg) scale(0.6);
  }
  100% {
    -webkit-transform: rotate(360deg) scale(1);
    transform: rotate(360deg) scale(1);
  }
}
</style>

